// src/components/DocumentUploader.jsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { sendDocumentAsAgent } from '../services/messageService';

/**
 * Componente para cargar y enviar documentos
 * Admite PDFs, documentos de Office, imágenes y archivos de texto
 */
export default function DocumentUploader({ conversationId, disabled = false }) {
  const { t } = useTranslation();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tamaño de archivo (16 MB max - límite de WhatsApp)
    const maxSize = 16 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('document.error.size') || 'El archivo es demasiado grande (máximo 16 MB)');
      return;
    }

    // Validar tipo de archivo
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError(t('document.error.type') || 'Tipo de archivo no permitido');
      return;
    }

    setSelectedFile(file);
    setError('');
    setUploadOpen(true);
  };

  const handleSendDocument = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError('');
      await sendDocumentAsAgent(conversationId, selectedFile, caption);

      // Reset state
      setUploadOpen(false);
      setSelectedFile(null);
      setCaption('');
    } catch (err) {
      console.error('[DocumentUploader] Error sending document:', err);
      setError(t('document.error.send') || err.message || 'Error enviando documento');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setUploadOpen(false);
    setSelectedFile(null);
    setCaption('');
    setError('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType.includes('word')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📽️';
    if (fileType.startsWith('text/')) return '📃';
    return '📎';
  };

  const uploadModal = uploadOpen && selectedFile && createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 relative"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl"
        >
          ✕
        </button>

        <h2 className="text-2xl font-semibold mb-6">
          {t('document.upload.title') || 'Enviar Documento'}
        </h2>

        {/* File info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{getFileIcon(selectedFile.type)}</span>
            <div className="flex-1">
              <p className="font-medium text-gray-800">{selectedFile.name}</p>
              <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
            </div>
          </div>
        </div>

        {/* Caption input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('document.caption.label') || 'Mensaje opcional'}
          </label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t('document.caption.placeholder') || 'Escribe un mensaje para acompañar el documento...'}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows="3"
            maxLength="1024"
            disabled={uploading}
          />
          <p className="text-xs text-gray-500 mt-1">
            {caption.length}/1024 {t('document.caption.characters') || 'caracteres'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
            disabled={uploading}
          >
            {t('common.cancel') || 'Cancelar'}
          </button>
          <button
            onClick={handleSendDocument}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
            disabled={uploading}
          >
            {uploading ? (t('document.sending') || 'Enviando...') : (t('document.send') || 'Enviar documento')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <input
        type="file"
        onChange={handleFileSelect}
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
        id="document-upload-input"
        disabled={disabled}
      />
      <label
        htmlFor={disabled ? undefined : "document-upload-input"}
        className={`inline-block p-2 rounded transition-colors ${
          disabled
            ? 'text-gray-400 cursor-not-allowed opacity-50'
            : 'cursor-pointer text-gray-600 hover:text-blue-600 hover:bg-blue-50'
        }`}
        title={disabled ? 'No puedes adjuntar documentos (esperando mensaje del cliente o conversación inactiva)' : (t('document.title') || 'Adjuntar Documento')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
          />
        </svg>
      </label>

      {uploadModal}
    </>
  );
}
